import { ScheduleServiceDialog } from "@/components/site/ScheduleServiceDialog";
import { Star, Phone, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { SITE_PHONE_DISPLAY, SITE_MAP_URL } from "@/routes-data";
import { useEffect, useState } from "react";
import { useCopy } from "@/i18n/LocaleContext";

const GOOGLE_REVIEW_LINK = "https://g.page/r/Cbu2CG9ARSvyEBM/review";

const COPY = {
  en: {
    home: "Home",
    reviews: "Reviews",
    seoTitle: "Axle Inc. Reviews - Elkhart Trailer Repair & Axle Shop",
    seoDesc: "Read real Google reviews of Axle Inc. - trailer axle repair, parts, and same-day service in Elkhart, IN. See why trailer owners and fleets across northern Indiana trust us. Leave your review in 30 seconds.",
    lastUpdated: "Last updated: July 2026",
    heroHeading: "Axle Inc. Reviews: What Elkhart Trailer Owners Say About Us",
    heroBody: "Trailer owners, RV haulers, and fleet managers across Elkhart County have trusted Axle Inc. since 1995 for axle repair, Dexter parts, and same-day service.",
    googleReviews: (count: number) => `${count} Google reviews`,
    leaveReview: "Leave us a review",
    scheduleService: "Schedule service",
    whyChooseHeading: "Why trailer owners choose Axle Inc.",
    whyChooseBody: "Three things come up again and again in our reviews: speed, straight answers, and parts on the shelf.",
    highlights: [
      { title: "Same-day service, most jobs.", desc: "Bearing repacks, brake jobs, and standard axle swaps are usually done the day you drop off - because the parts are already in stock." },
      { title: "The region's authorized Dexter Group distributor.", desc: "Genuine OEM axles and components from 1,000 to 10,000 lb capacity, with current Dexter rebates passed through to you." },
      { title: "A shop run by people who fix trailers, not just sell parts.", desc: "60+ years of combined hands-on experience behind the counter and in the bays." }
    ],
    readOnGoogle: "Read our reviews on Google",
    recentReviews: "Recent Google reviews",
    recentReviewsDesc: "Pulled live from our Google Business Profile and shown unedited.",
    viewOnGoogle: "View on Google",
    servicesHeading: "Reviews by service",
    serviceHeader: "Service",
    highlightHeader: "What customers highlight",
    serviceRows: [
      { service: "Axle repair & replacement", highlight: "Turnaround time, correct diagnosis, fair pricing" },
      { service: "Parts counter", highlight: "Dexter parts in stock, help matching the right part" },
      { service: "Custom axle builds", highlight: "Built-to-spec accuracy, communication" },
      { service: "Fleet & dealer service", highlight: "Scheduling flexibility, consistency across units" }
    ],
    hadServiceHeading: "Had service with us? Tell the next trailer owner.",
    hadServiceBody: "A 30-second review helps neighbors across Elkhart County find a shop they can trust - and tells us what to keep doing (or fix).",
    steps: ["Tap Leave us a review (or scan the QR card from the counter).", "Pick your stars.", "One or two sentences: what you brought in, how it went."],
    feedbackHeading: "How we handle feedback",
    feedbackBody: "We read and respond to every review, usually within two business days. If something wasn't right, call (574) 264-9434 and ask for the service desk before you head back - we'd rather fix it than debate it.",
    faqHeading: "Frequently asked questions",
    faqs: [
      { q: "Where can I read Axle Inc. reviews?", a: "Our Google Business Profile has all of our reviews. You'll also find Axle Inc. on Facebook and the BBB. We display reviews unedited and respond to every one." },
      { q: "How do I leave a review?", a: "Tap the \"Leave us a review\" button on this page (it opens Google's one-tap review form), or scan the QR card at our counter. It takes about 30 seconds and you don't need to write an essay - stars and a sentence help plenty." },
      { q: "Do you respond to negative reviews?", a: "Yes - every review gets a response from our team, positive or negative. If we got something wrong, we'll say so and make it right. Call (574) 264-9434 if you'd rather talk it through first." }
    ],
    readyHeading: "Ready when your trailer is.",
    address: "53664 County Road 9, Elkhart, IN · Mon–Fri 8am–5pm",
    scheduleOnline: "Schedule online",
    starLabel: (r: number) => `${r} out of 5 stars`
  },
  es: {
    home: "Inicio",
    reviews: "Reseñas",
    seoTitle: "Reseñas de Axle Inc. - Taller de Reparación de Remolques y Ejes en Elkhart",
    seoDesc: "Lea reseñas reales de Google sobre Axle Inc. - reparación de ejes de remolque, piezas y servicio en el mismo día en Elkhart, IN. Vea por qué los propietarios de remolques y flotas en todo el norte de Indiana confían en nosotros. Deje su reseña en 30 segundos.",
    lastUpdated: "Última actualización: julio de 2026",
    heroHeading: "Reseñas de Axle Inc.: Lo que dicen los propietarios de remolques de Elkhart sobre nosotros",
    heroBody: "Los propietarios de remolques, transportistas de RV y administradores de flotas en todo el condado de Elkhart han confiado en Axle Inc. desde 1995 para la reparación de ejes, piezas Dexter y servicio en el mismo día.",
    googleReviews: (count: number) => `${count} reseñas en Google`,
    leaveReview: "Déjenos una reseña",
    scheduleService: "Programar servicio",
    whyChooseHeading: "Por qué los propietarios de remolques eligen Axle Inc.",
    whyChooseBody: "Tres cosas destacan una y otra vez en nuestras reseñas: velocidad, respuestas claras y piezas en existencia.",
    highlights: [
      { title: "Servicio en el mismo día en la mayoría de los trabajos.", desc: "El reempacado de rodamientos, trabajos de frenos y cambios de eje estándar suelen realizarse el mismo día que nos deja el equipo, porque las piezas ya están en inventario." },
      { title: "El distribuidor autorizado de Dexter Group en la región.", desc: "Ejes y componentes originales OEM con capacidad de 1,000 a 10,000 libras, con los descuentos actuales de Dexter aplicados a usted." },
      { title: "Un taller dirigido por personas que reparan remolques, no solo venden piezas.", desc: "Más de 60 años de experiencia combinada y práctica detrás del mostrador y en las bahías de servicio." }
    ],
    readOnGoogle: "Lea nuestras reseñas en Google (en inglés)",
    recentReviews: "Reseñas recientes en Google",
    recentReviewsDesc: "Obtenidas directamente de nuestro Perfil de Negocio en Google y mostradas sin editar.",
    viewOnGoogle: "Ver en Google (en inglés)",
    servicesHeading: "Reseñas por servicio",
    serviceHeader: "Servicio",
    highlightHeader: "Lo que destacan los clientes",
    serviceRows: [
      { service: "Reparación y reemplazo de ejes", highlight: "Tiempo de respuesta, diagnóstico correcto, precios justos" },
      { service: "Mostrador de piezas", highlight: "Piezas Dexter en stock, ayuda para encontrar la pieza correcta" },
      { service: "Construcción de ejes a medida", highlight: "Precisión según especificaciones, comunicación" },
      { service: "Servicio a flotas y distribuidores", highlight: "Flexibilidad de programación, consistencia entre unidades" }
    ],
    hadServiceHeading: "¿Recibió servicio con nosotros? Cuénteselo al próximo propietario de un remolque.",
    hadServiceBody: "Una reseña de 30 segundos ayuda a los vecinos del condado de Elkhart a encontrar un taller en el que puedan confiar, y nos indica qué seguir haciendo (o qué arreglar).",
    steps: ["Toque Déjenos una reseña (o escanee la tarjeta QR del mostrador).", "Elija sus estrellas.", "Una o dos oraciones: qué trajo y cómo fue el servicio."],
    feedbackHeading: "Cómo manejamos los comentarios",
    feedbackBody: "Leemos y respondemos a cada reseña, generalmente dentro de dos días hábiles. Si algo no estuvo bien, llame al (574) 264-9434 y pregunte por el departamento de servicio antes de regresar; preferimos arreglarlo que discutirlo.",
    faqHeading: "Preguntas frecuentes",
    faqs: [
      { q: "¿Dónde puedo leer las reseñas de Axle Inc.?", a: "Nuestro Perfil de Negocio en Google tiene todas nuestras reseñas. También encontrará a Axle Inc. en Facebook y el BBB. Mostramos las reseñas sin editar y respondemos a cada una." },
      { q: "¿Cómo dejo una reseña?", a: "Toque el botón \"Déjenos una reseña\" en esta página (abre el formulario de reseña de un solo toque de Google) o escanee la tarjeta QR en nuestro mostrador. Toma unos 30 segundos y no necesita escribir un ensayo: las estrellas y una oración ayudan mucho." },
      { q: "¿Responden a las reseñas negativas?", a: "Sí, cada reseña recibe una respuesta de nuestro equipo, positiva o negativa. Si cometimos un error, lo admitiremos y lo corregiremos. Llame al (574) 264-9434 si prefiere hablarlo primero." }
    ],
    readyHeading: "Listos cuando su remolque lo esté.",
    address: "53664 County Road 9, Elkhart, IN · Lun–Vie 8am–5pm",
    scheduleOnline: "Programar en línea",
    starLabel: (r: number) => `${r} de 5 estrellas`
  }
};

export default function Reviews() {
  const t = useCopy(COPY);
  const trail = [
    { name: t.home, path: "/" },
    { name: t.reviews, path: "/reviews/" },
  ];
  const feed = useGoogleReviews();
  const jsonLd: object[] = [breadcrumbJsonLd(trail)];
  jsonLd.push(feed ? reviewsJsonLd(feed) : organizationJsonLd());
  
  return (
    <PageLayout>
      <Seo
        title={t.seoTitle}
        description={t.seoDesc}
        path="/reviews/"
        jsonLd={jsonLd}
      />

      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4">{t.reviews}</p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-4xl">
            {t.heroHeading}
          </h1>
          <p className="mt-4 text-sm text-secondary-foreground/60">{t.lastUpdated}</p>
          <p className="mt-5 text-lg text-secondary-foreground/85 max-w-3xl leading-relaxed">
            {t.heroBody}
          </p>
          {feed && feed.rating != null && feed.reviewCount != null && (
            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="live-rating-summary">
              <StarRow rating={feed.rating} className="h-5 w-5" />
              <span className="text-xl font-bold">{feed.rating.toFixed(1)}</span>
              <a
                href={feed.mapsUrl ?? SITE_MAP_URL}
                target="_blank"
                rel="noopener"
                className="text-sm text-secondary-foreground/80 hover:text-white underline underline-offset-4"
              >
                {t.googleReviews(feed.reviewCount)}
              </a>
            </div>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <a href={GOOGLE_REVIEW_LINK} target="_blank" rel="noopener">
                <Star className="mr-1.5 h-4 w-4" /> {t.leaveReview}
              </a>
            </Button>
            <ScheduleServiceDialog>
              <Button size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
                {t.scheduleService}
              </Button>
            </ScheduleServiceDialog>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 max-w-3xl">
          {t.whyChooseHeading}
        </h2>
        <p className="text-muted-foreground max-w-3xl mb-10">
          {t.whyChooseBody}
        </p>
        <div className="grid gap-6 md:grid-cols-3">
          {t.highlights.map((h: any) => (
            <article key={h.title} className="border border-border rounded-md p-6 bg-card">
              <Star className="h-5 w-5 text-primary mb-3" />
              <h3 className="font-semibold mb-2">{h.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{h.desc}</p>
            </article>
          ))}
        </div>
        <p className="mt-8 text-sm text-muted-foreground">
          <a href={SITE_MAP_URL} target="_blank" rel="noopener" className="text-primary-strong hover:underline underline-offset-4 inline-flex items-center gap-1">
            {t.readOnGoogle} <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </p>
      </section>

      {feed && feed.reviews.length > 0 && (
        <section className="container mx-auto px-4 pb-16 md:pb-20" data-testid="recent-reviews">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">{t.recentReviews}</h2>
          <p className="text-muted-foreground mb-8 max-w-3xl">
            {t.recentReviewsDesc}
          </p>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {feed.reviews.map((r: any) => (
              <article key={`${r.author}-${r.publishTime}`} className="border border-border rounded-md p-6 bg-card flex flex-col">
                <div className="flex items-center gap-3 mb-3">
                  {r.authorPhotoUrl ? (
                    <img src={r.authorPhotoUrl} alt="" loading="lazy" referrerPolicy="no-referrer" className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <span className="h-10 w-10 rounded-full bg-muted flex items-center justify-center font-semibold text-muted-foreground">
                      {r.author.charAt(0)}
                    </span>
                  )}
                  <div>
                    <p className="font-semibold text-sm">{r.author}</p>
                    <p className="text-xs text-muted-foreground">{r.relativeTime}</p>
                  </div>
                </div>
                <StarRow rating={r.rating} />
                {r.text && (
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{r.text}</p>
                )}
                {r.reviewUrl && (
                  <a href={r.reviewUrl} target="_blank" rel="noopener" className="mt-auto pt-4 text-xs text-primary-strong hover:underline underline-offset-4 inline-flex items-center gap-1">
                    {t.viewOnGoogle} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">{t.servicesHeading}</h2>
          <div className="overflow-x-auto">
            <table className="w-full max-w-3xl text-sm border border-border bg-card rounded-md">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="p-4 font-semibold">{t.serviceHeader}</th>
                  <th className="p-4 font-semibold">{t.highlightHeader}</th>
                </tr>
              </thead>
              <tbody>
                {t.serviceRows.map((r: any) => (
                  <tr key={r.service} className="border-b border-border last:border-0">
                    <td className="p-4 font-medium">{r.service}</td>
                    <td className="p-4 text-muted-foreground">{r.highlight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">
          {t.hadServiceHeading}
        </h2>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          {t.hadServiceBody}
        </p>
        <ol className="list-decimal pl-5 space-y-2 text-muted-foreground mb-8">
          {t.steps.map((step: string, i: number) => <li key={i}>{step}</li>)}
        </ol>
        <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
          <a href={GOOGLE_REVIEW_LINK} target="_blank" rel="noopener">
            <Star className="mr-1.5 h-4 w-4" /> {t.leaveReview}
          </a>
        </Button>

        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mt-14 mb-4">{t.feedbackHeading}</h2>
        <p className="text-muted-foreground leading-relaxed">
          {t.feedbackBody}
        </p>
      </section>

      <section className="container mx-auto px-4 pb-16 md:pb-20 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">{t.faqHeading}</h2>
        <div className="divide-y divide-border border-y border-border">
          {t.faqs.map((f: any) => (
            <details key={f.q} className="group py-5">
              <summary className="cursor-pointer list-none flex justify-between items-start gap-4 font-medium">
                {f.q}
                <span className="text-primary text-xl leading-none mt-0.5 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="bg-primary-deep text-primary-foreground">
        <div className="container mx-auto px-4 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold">{t.readyHeading}</h2>
            <p className="mt-1 text-primary-foreground">
              {t.address}
            </p>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <ScheduleServiceDialog>
              <Button size="lg" className="bg-white text-primary-strong hover:bg-white/90">{t.scheduleOnline}</Button>
            </ScheduleServiceDialog>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}

const REVIEWS_FEED_URL = "/api/google-reviews";

function useGoogleReviews(): GoogleReviewsFeed | null {
  const [feed, setFeed] = useState<GoogleReviewsFeed | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch(REVIEWS_FEED_URL)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: GoogleReviewsFeed | null) => {
        if (!cancelled && data && data.available) setFeed(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return feed;
}

function reviewsJsonLd(feed: GoogleReviewsFeed) {
  return {
    ...organizationJsonLd(),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: feed.rating,
      reviewCount: feed.reviewCount,
      bestRating: 5,
      worstRating: 1,
    },
    review: feed.reviews.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author },
      reviewRating: {
        "@type": "Rating",
        ratingValue: r.rating,
        bestRating: 5,
        worstRating: 1,
      },
      reviewBody: r.text,
      datePublished: r.publishTime,
    })),
  };
}

interface GoogleReviewsFeed {
  available: boolean;
  rating?: number | null;
  reviewCount?: number | null;
  mapsUrl?: string | null;
  reviews: GoogleReview[];
}

function StarRow({ rating, className = "h-4 w-4" }: { rating: number; className?: string }) {
  const t = useCopy(COPY);
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={t.starLabel(rating)}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${className} ${i <= Math.round(rating) ? "text-primary fill-primary" : "text-muted-foreground/40"}`}
          aria-hidden="true"
        />
      ))}
    </span>
  );
}

interface GoogleReview {
  author: string;
  authorPhotoUrl?: string | null;
  rating: number;
  text: string;
  relativeTime: string;
  publishTime: string;
  reviewUrl?: string | null;
}
